import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

const CSS_FAILURE_FLAG = 'dfc_css_failure_reported';

function hasAlreadyReportedCssFailure(): boolean {
    try {
        return sessionStorage.getItem(CSS_FAILURE_FLAG) === '1';
    } catch {
        return false;
    }
}

function markCssFailureReported(): void {
    try {
        sessionStorage.setItem(CSS_FAILURE_FLAG, '1');
    } catch {
        // ignore
    }
}

function captureCssFailure(
    signal: 'main_stylesheet_load_failed' | 'css_canary_failed',
    extra: Record<string, unknown> = {}
): void {
    if (hasAlreadyReportedCssFailure()) {
        return;
    }

    markCssFailureReported();

    Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('signal', signal);
        scope.setTag('app', 'dashboard');
        scope.setContext('css_failure', {
            currentUrl: location.href,
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            readyState: document.readyState,
            ...extra,
        });
        scope.setFingerprint([signal]);
        Sentry.captureMessage(signal.toUpperCase());
    });
}

function installMainStylesheetFailureHook(): void {
    window.addEventListener(
        'error',
        (event: Event) => {
            const target = event.target as EventTarget | null;

            if (!(target instanceof HTMLLinkElement)) {
                return;
            }

            if (target.rel !== 'stylesheet') {
                return;
            }

            const href = target.href || '';
            const normalizedHref = href.split('?')[0].split('#')[0];

            // Only care about Angular's main styles bundle
            if (!/\/styles(\.[a-f0-9]{8,})?\.css$/i.test(normalizedHref)) {
                return;
            }

            captureCssFailure('main_stylesheet_load_failed', {
                href,
                normalizedHref,
            });
        },
        true
    );
}

function reportIfCssCanaryMissing(): void {
    const check = () => {
        const cssVar = getComputedStyle(document.documentElement)
            .getPropertyValue('--app-css-loaded')
            .trim();

        if (cssVar === '1') {
            return;
        }

        captureCssFailure('css_canary_failed', {
            cssVar: cssVar || '(missing)',
            stylesheets: Array.from(
                document.querySelectorAll('link[rel="stylesheet"]')
            ).map((el) => (el as HTMLLinkElement).href),
        });
    };

    if (document.readyState === 'complete') {
        requestAnimationFrame(check);
        return;
    }

    const timer = window.setTimeout(check, 8000);

    window.addEventListener(
        'load',
        () => {
            clearTimeout(timer);
            requestAnimationFrame(check);
        },
        { once: true }
    );
}

if (environment.production) {
    enableProdMode();
}

const sentryTestFlag =
    window.location.search.includes('sentryTest') ||
    window.location.hash.includes('sentryTest');

const sentryEnabled = environment.production || sentryTestFlag;

if (sentryEnabled) {
    Sentry.init({
        dsn: 'https://533770fc06598ab8b8710054cfcfeb63@o4507408409952256.ingest.us.sentry.io/4507408563175424',
        integrations: [Sentry.browserTracingIntegration()],
        tracePropagationTargets: [
            'dev.app2.dollarflightclub.com',
            'app2.dollarflightclub.com',
            'dev.app.dollarflightclub.com',
            'app.dollarflightclub.com',
            'localhost',
            /^\//,
        ],

        // normal error events
        sampleRate: environment.production ? 0.1 : 1.0,

        // performance traces only
        tracesSampleRate: environment.production ? 0.005 : 1.0,

        environment: environment.production ? 'production' : 'development',
        ignoreErrors: [/ResizeObserver loop limit exceeded/i],
        release: 'dashboard@1.1.0',

        beforeSend(event) {
            const msg =
                event.message ||
                event.exception?.values?.[0]?.value ||
                '';

            if (/chrome-extension:|moz-extension:/i.test(String(msg))) {
                return null;
            }

            return event;
        },
    });

    Sentry.setTag('app', 'dashboard');

    installMainStylesheetFailureHook();
    reportIfCssCanaryMissing();

    if (sentryTestFlag) {
        (window as any).Sentry = Sentry;
        Sentry.captureMessage('SENTRY_TEST_MESSAGE');
    }
}

platformBrowserDynamic()
    .bootstrapModule(AppModule)
    .catch((err) => {
        console.error(err);
    });
